import { useMemo } from "react";

import type {
  ScreenMembersOnSignupPassword,
  SignupPasswordOptions,
} from "@auth0/auth0-acul-js/signup-password";
import SignupPasswordInstance from "@auth0/auth0-acul-js/signup-password";

import { executeSafely } from "../utils/helpers/executeSafely";

export const useSignupPasswordManager = () => {
  const signupPasswordInstance = useMemo(() => new SignupPasswordInstance(), []);

  const { transaction, screen } = signupPasswordInstance;
  const {
    passwordPolicy,
    requiredIdentifiers,
    optionalIdentifiers,
    alternateConnections,
    isPasskeyEnabled,
  } = transaction;
  const { loginLink, editLink, texts, captchaImage, data } = screen;

  const identifierData = {
    email: data?.email ?? "",
    username: data?.username ?? "",
    phoneNumber: data?.phone_number ?? "",
  };

  const handleSignupPassword = async (
    password: string,
    captcha?: string
  ): Promise<void> => {
    const options: SignupPasswordOptions = {
      email: identifierData.email || undefined,
      username: identifierData.username || undefined,
      phone: identifierData.phoneNumber || undefined,
      password: password || "",
    };

    if (screen.isCaptchaAvailable && captcha?.trim()) {
      options.captcha = captcha.trim();
    }

    executeSafely(
      `SignupPassword with options: ${JSON.stringify({
        ...options,
        password: "********",
      })}`,
      () => signupPasswordInstance.signup(options)
    );
  };

  const handleFederatedSignup = async (connectionName: string) => {
    executeSafely(
      `Federated signup with connection: ${connectionName}`,
      () => signupPasswordInstance.federatedSignup({ connection: connectionName })
    );
  };

  return {
    signupPasswordInstance,
    handleSignupPassword,
    handleFederatedSignup,
    texts: (texts || {}) as ScreenMembersOnSignupPassword["texts"],
    errors: signupPasswordInstance.getError(),
    isCaptchaAvailable: screen.isCaptchaAvailable === true,
    captchaImage,
    passwordPolicy,
    requiredIdentifiers: requiredIdentifiers ?? [],
    optionalIdentifiers: optionalIdentifiers ?? [],
    alternateConnections: alternateConnections ?? [],
    isPasskeyEnabled: isPasskeyEnabled === true,
    loginLink: loginLink || null,
    editIdentifierLink: editLink || null,
    identifierData,
  };
};
